import { useState } from "react";
import { api } from "../lib/api";
import type { AppConfig, AssessmentPayload, SessionInfo } from "../lib/types";
import PatientView from "./PatientView";

const SYMPTOMS: [string, string][] = [
  ["snoring", "Snoring"], ["daytime_sleepiness", "Daytime sleepiness"], ["persistent_headache", "Persistent headache"],
  ["hip_groin_thigh_pain", "Hip/groin/thigh pain"], ["knee_pain", "Knee pain"], ["limp", "Limp"],
  ["polyuria_polydipsia", "Polyuria/polydipsia"], ["oligomenorrhea", "Oligomenorrhea"], ["hirsutism", "Hirsutism"],
  ["hyperphagia", "Hyperphagia"], ["binge_eating", "Binge eating"], ["purging_or_restricting", "Purging/restricting"],
  ["depressed_mood", "Depressed mood"],
];
const RISKS: [string, string][] = [
  ["family_history_t2dm", "Family hx T2DM"], ["maternal_diabetes_or_gdm", "Maternal DM/GDM"],
  ["acanthosis_nigricans", "Acanthosis nigricans"], ["family_history_dyslipidemia", "Family hx dyslipidemia"],
  ["parental_consanguinity", "Parental consanguinity"],
];
// Units follow the site setting (UAE default: SI). HbA1c is entered in % (NGSP).
const LABS_SI: [string, string, string][] = [
  ["ldl", "LDL", "mmol/L"], ["hdl", "HDL", "mmol/L"], ["tg", "Triglycerides", "mmol/L"],
  ["hba1c", "HbA1c", "%"], ["fpg", "Fasting glucose", "mmol/L"], ["alt", "ALT", "U/L"],
];
const LABS_CONV: [string, string, string][] = [
  ["ldl", "LDL", "mg/dL"], ["hdl", "HDL", "mg/dL"], ["tg", "Triglycerides", "mg/dL"],
  ["hba1c", "HbA1c", "%"], ["fpg", "Fasting glucose", "mg/dL"], ["alt", "ALT", "U/L"],
];

export default function ManualEntry({ config, session }: { config: AppConfig | null; session: SessionInfo }) {
  const [form, setForm] = useState({ birth_date: "", sex: "female", height_cm: "", weight_kg: "", systolic: "", diastolic: "" });
  const [labs, setLabs] = useState<Record<string, string>>({});
  const [sym, setSym] = useState<Set<string>>(new Set());
  const [risk, setRisk] = useState<Set<string>>(new Set());
  const [meds, setMeds] = useState("");
  const [result, setResult] = useState<AssessmentPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, k: string) => {
    const n = new Set(set);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setter(n);
  };
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const labUnits = config?.region?.lab_units ?? "si";
  const LABS = labUnits === "si" ? LABS_SI : LABS_CONV;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      const r = await api.assessManual({
        birth_date: form.birth_date,
        sex: form.sex,
        height_cm: num(form.height_cm),
        weight_kg: num(form.weight_kg),
        systolic: num(form.systolic),
        diastolic: num(form.diastolic),
        labs: Object.fromEntries(Object.entries(labs).filter(([, v]) => v.trim() !== "").map(([k, v]) => [k, Number(v)])),
        lab_units: labUnits,
        symptoms: [...sym],
        risk_factors: [...risk],
        medications: meds.split(/[,;\n]/).map((m) => m.trim()).filter(Boolean),
      });
      setResult(r);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    }
  }

  if (result) {
    return (
      <div className="stack">
        <button className="btn" style={{ alignSelf: "flex-start" }} onClick={() => setResult(null)}>
          ← New entry
        </button>
        <PatientView patientId={result.patient.id} initial={result} config={config} session={session} />
      </div>
    );
  }

  const input = (k: keyof typeof form, label: string, type = "number") => (
    <label className="field">
      {label}
      <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} step="any"
        inputMode={type === "number" ? "decimal" : undefined} />
    </label>
  );

  return (
    <form className="card stack" onSubmit={submit} style={{ maxWidth: 860 }}>
      <h2>Manual entry</h2>
      <p className="muted small" style={{ marginTop: -6 }}>
        For clinics without EHR integration. Values are evaluated with the same engine and kept for the configured
        retention period (default 30 days) so the patient can be reopened; enter no names, Emirates IDs or MRNs here.
      </p>
      <div className="form-grid">
        {input("birth_date", "Date of birth", "date")}
        <label className="field">
          Sex
          <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </label>
        {input("height_cm", "Height (cm)")}
        {input("weight_kg", "Weight (kg)")}
        {input("systolic", "Systolic BP")}
        {input("diastolic", "Diastolic BP")}
      </div>
      <fieldset style={{ border: 0, padding: 0 }}>
        <legend className="small muted">Labs (optional, today)</legend>
        <div className="form-grid">
          {LABS.map(([k, l, u]) => (
            <label key={k} className="field">
              {l} ({u})
              <input type="number" step="any" inputMode="decimal" value={labs[k] ?? ""} onChange={(e) => setLabs({ ...labs, [k]: e.target.value })} />
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset style={{ border: 0, padding: 0 }}>
        <legend className="small muted">Review of systems (CPG Table 4)</legend>
        <div className="filters">
          {SYMPTOMS.map(([k, l]) => (
            <button type="button" key={k} className="chip" aria-pressed={sym.has(k)} onClick={() => toggle(sym, setSym, k)}>
              {l}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset style={{ border: 0, padding: 0 }}>
        <legend className="small muted">Risk factors</legend>
        <div className="filters">
          {RISKS.map(([k, l]) => (
            <button type="button" key={k} className="chip" aria-pressed={risk.has(k)} onClick={() => toggle(risk, setRisk, k)}>
              {l}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="field">
        Current medications (comma-separated)
        <input type="text" value={meds} onChange={(e) => setMeds(e.target.value)} placeholder="e.g. risperidone, montelukast" />
      </label>
      {err && <p className="error">{err}</p>}
      <div>
        <button className="btn primary" disabled={!form.birth_date || !form.height_cm || !form.weight_kg}>
          Evaluate
        </button>
      </div>
    </form>
  );
}
